import type { LocationGenerics } from "@/router/locationGenerics";

import { FeatureGuardFallbackWithOverlay } from "@/components/elements/FeatureGuard";
import { ModelBreadcrumb } from "@/components/elements/breadcrumbs/ModelBreadcrumb";
import { WorkflowBreadcrumb } from "@/components/elements/breadcrumbs/WorkflowBreadcrumb";
import { WorkflowRunBreadcrumb } from "@/components/elements/breadcrumbs/WorkflowRunBreadcrumb";
import {
  OrchestrationFeatureGuard,
  OrchestrationTeaser,
} from "@/features/feature-guards";
import { ApolloClient } from "@apollo/client";
import { Navigate, Outlet, Route } from "@tanstack/react-location";

export const workflowRoute = (
  _client: ApolloClient<object>,
): Route<LocationGenerics> => {
  return {
    path: "orchestration",
    element: (
      <OrchestrationFeatureGuard
        fallback={
          <FeatureGuardFallbackWithOverlay>
            <OrchestrationTeaser />
          </FeatureGuardFallbackWithOverlay>
        }
      >
        <Outlet />
      </OrchestrationFeatureGuard>
    ),
    meta: {
      breadcrumb: () => "Orchestration",
    },
    children: [
      {
        path: "independent",
        element: () => import("./LocalJobs").then((m) => <m.default />),
        meta: {
          breadcrumb: () => "Local Jobs",
        },
      },
      {
        path: ":workflowId",
        meta: {
          breadcrumb: ({ workflowId }) => (
            <WorkflowBreadcrumb workflowId={workflowId} />
          ),
        },
        children: [
          {
            path: "history",
            meta: {
              breadcrumb: () => "History",
            },
            children: [
              {
                path: ":workflowRunId",
                element: () =>
                  import("./HistoricGlobalOrchestrationRun").then((m) => (
                    <m.default />
                  )),
                meta: {
                  breadcrumb: ({ workflowRunId }) => (
                    <WorkflowRunBreadcrumb workflowRunId={workflowRunId} />
                  ),
                },
              },
              {
                element: () =>
                  import("./OrchestrationHistory").then((m) => <m.default />),
              },
            ],
          },
          {
            path: "webhooks",
            meta: {
              breadcrumb: () => "Webhooks",
            },
            children: [
              {
                element: () =>
                  import("./Webhook/OrchestrationWebHooks").then((m) => (
                    <m.OrchestrationWebHooks />
                  )),
              },
            ],
          },
          {
            path: "configuration",
            element: () =>
              import("./WorkflowSettings").then((m) => <m.default />),
            meta: {
              breadcrumb: () => "Configuration",
            },
          },
          {
            path: "model",
            children: [
              {
                path: ":modelId",
                element: () =>
                  import("./ModelDetails").then((m) => <m.default />),
                meta: {
                  breadcrumb: ({ modelId }) => (
                    <ModelBreadcrumb modelId={modelId} />
                  ),
                },
              },
              {
                // TODO: this is a hack to have the <BreadCrumbs /> component work
                // The model path without a :modelId does not exist, but will be added
                // to the breadcrumbs regardless.
                element: <Navigate to=".." replace search />,
              },
            ],
          },
          {
            element: () =>
              import("./OrchestrationWorkflow").then((m) => <m.default />),
          },
        ],
      },
      {
        element: () =>
          import("./OrchestrationOverview").then((m) => <m.default />),
      },
    ],
  };
};
